import type { ReactNode } from 'react';
import { DropdownMenu } from 'radix-ui';
import { cn } from '../../lib/cn.js';

/** Row-level and page-level action menus. Radix handles focus, typeahead and Esc. */

export function Menu({ trigger, children, align = 'end' }: {
  trigger: ReactNode;
  children: ReactNode;
  align?: 'start' | 'center' | 'end';
}) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align={align}
          sideOffset={6}
          className={cn(
            'pop z-50 min-w-44 overflow-hidden rounded-lg border border-border bg-surface-raised p-1 shadow-lg',
          )}
        >
          {children}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export function MenuItem({
  children,
  onSelect,
  destructive,
  disabled,
  icon,
}: {
  children: ReactNode;
  onSelect?: () => void;
  destructive?: boolean;
  disabled?: boolean;
  icon?: ReactNode;
}) {
  return (
    <DropdownMenu.Item
      disabled={disabled}
      onSelect={onSelect}
      className={cn(
        'flex cursor-pointer select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none',
        'data-[highlighted]:bg-surface-sunken data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        destructive
          ? 'text-danger data-[highlighted]:bg-danger-subtle'
          : 'text-foreground',
      )}
    >
      {icon ? <span className="[&_svg]:size-4 text-muted-foreground">{icon}</span> : null}
      {children}
    </DropdownMenu.Item>
  );
}

export function MenuSeparator() {
  return <DropdownMenu.Separator className="my-1 h-px bg-border" />;
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <DropdownMenu.Label className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-subtle-foreground">
      {children}
    </DropdownMenu.Label>
  );
}
